import {I18n} from '@aws-amplify/core';
import {Badge} from '@mantine/core';

import {ReportRun} from '../types';

/**
 * FNP-918: the report run `status` as a badge rather than a raw string (§13).
 *
 * ⚠️ Colour is NOT the only carrier of meaning — the badge always renders the
 * status text, so it survives a colourblind reader and a greyscale print
 * (§13 Accessibility).
 *
 * Colours are Mantine THEME TOKENS, not hex literals, so both light and dark
 * themes work (§2). The sibling badges in this app (invoices, prices,
 * payment-intents, setup-intents, subscriptions) hardcode hex values like
 * '#dc3545'; they are out of scope here and left alone, but do NOT copy them.
 * Same call as customers/profile/DelinquentBadge.
 *
 * Stripe's report run statuses are `pending`, `succeeded` and `failed`. An
 * unrecognised value renders grey with its own text rather than being forced
 * into one of the three — a status we do not know about is not an error, and
 * mislabelling it would be worse than showing it verbatim.
 */
const STATUS_COLORS: Record<string, string> = {
    succeeded: 'green',
    pending: 'yellow',
    failed: 'red',
};

export const StatusBadge = ({record}: {record: ReportRun}) => {
    const status = record.status;

    if (!status) {
        // Nullable field renders the placeholder, never blank (§13).
        return <>{'—'}</>;
    }

    return (
        <Badge
            color={STATUS_COLORS[status.toLowerCase()] ?? 'gray'}
            variant='light'
        >
            {I18n.get(status)}
        </Badge>
    );
};
